/* Common Imports */

import React from "react";

/* Authentication Imports */

import { SessionProvider, signOut } from "next-auth/react";

/* Vercel Analytics Imports */

import { Analytics } from "@vercel/analytics/react";

/* Global CSS Imports */

import "../public/fonts/noto-sans-kannada/noto-sans-kannada.css";
import "../public/fonts/poppins/poppins.css";
import "../public/fonts/readex-pro/readex-pro.css";
import "../public/styles/animation/agentpropertylist.css";
import "../public/styles/animation/allstarprofessional.css";
import "../public/styles/animation/analyticssucceeding.css";
import "../public/styles/animation/boostcredibility.css";
import "../public/styles/animation/directconnectuser.css";
import "../public/styles/animation/introducepartiesandclaimes.css";
import "../public/styles/animation/listpropertybuilder.css";
import "../public/styles/animation/loading.css";
import "../public/styles/animation/ofbuser.css";
import "../public/styles/animation/personalbranding.css";
import "../public/styles/animation/professionalrecommendation.css";
import "../public/styles/animation/professionalservices.css";
import "../public/styles/animation/rockstarlandowner.css";
import "../public/styles/animation/skyrocketcareer.css";
import "../public/styles/animation/topleadsagents.css";
import "../public/styles/animation/userlocationbased.css";
import "../public/styles/animation/userpropertysearch.css";
import "../public/styles/index.css";

/* Theme Imports */

import { CacheProvider, EmotionCache } from "@emotion/react";
import { PaletteMode, createTheme, useMediaQuery } from "@mui/material";
import { LicenseInfo } from "@mui/x-license";
import CssBaseline from "@mui/material/CssBaseline";
import { GlobalStyles } from "@mui/material";
import { ThemeProvider } from "@mui/material/styles";
import { Cookies, CookiesProvider, useCookies } from "react-cookie";
import CreateEmotionCache from "../components/layout-components/CreateEmotionCache";
import GetDesignTokens from "../components/layout-components/Theme";
import ColorModeContext from "../components/layout-components/contexts/colorModeContext";

/* Function Imports */

// import ReceiveMessage from "../lib/ReceiveMessage";

/* Redux Imports */

import { ObjectId } from "bson";
import { Provider } from "react-redux";
import { validateClientSession } from "../lib/auth/helpers";
import { setThemeThunk, wrapper } from "../redux-magic/store";

/* MUI X Pro License */

/*

* Set the MUI X Pro license key once, globally, so every Pro feature
* (date-pickers-pro, etc.) is licensed app-wide regardless of which page loads.

*/

LicenseInfo.setLicenseKey(process.env.MUI_PRO_LICENSE_KEY || "");

/*

^ Feature Switches

*/

/*

* Shows a dialog box to the user if the websocket connection fails.
* Possible cases include:

* 1. The user's internet connection is down.
* 2. The server is down.
* 3. The browser has paused the tab's thread (user is inactive).

! Boolean switch for inactivity dialog feature. Turned off on Tuesday, March the 4th 2025 at 01:00:00 AM.

*/

const FEATURE_SWITCH_INACTIVITY_DIALOG: boolean = false;

export function reportWebVitals(metric: any) {}

const clientSideEmotionCache = CreateEmotionCache();

const Marketplace = ({ Component, ...everythingElse }: { Component: any; everythingElse: any }) => {
	const {
		store,
		props: { emotionCache = clientSideEmotionCache, pageProps, themeSetting },
	}: {
		store: any;
		props: {
			emotionCache?: EmotionCache | undefined;
			pageProps: any;
			themeSetting: PaletteMode;
		};
	} = wrapper.useWrappedStore(everythingElse);

	const session: any = pageProps.session ? pageProps.session : pageProps.server_session;

	const [mode, setMode] = React.useState<PaletteMode>(themeSetting || "light");

	const colorMode = React.useMemo(
		() => ({
			toggleColorMode: () => {
				setMode((prevMode: PaletteMode) => {
					const newMode = prevMode === "light" ? "dark" : "light";
					setThemeThunk(newMode);
					return newMode;
				});
			},
		}),
		[],
	);

	const [cookies, setCookie] = useCookies(["cookieColorMode"]);
	const prefersLightMode = useMediaQuery("(prefers-color-scheme: light)");
	const isBrowser = typeof window !== "undefined";

	const addDays = (date: Date, days: number) => {
		const copy = new Date(Number(date));
		copy.setDate(date.getDate() + days);
		return copy;
	};

	React.useEffect(() => {
		if (validateClientSession(session)) {
			setMode(session.user.dbUser.settings.site_preferences.default_theme);
		}
		// eslint-disable-next-line react-hooks/exhaustive-deps
	}, []);

	React.useEffect(() => {
		if (prefersLightMode && !!cookies.cookieColorMode !== true) {
			setMode("light");
		}
	}, [prefersLightMode, cookies.cookieColorMode]);

	const firstUpdate = React.useRef(true);

	React.useEffect(() => {
		if (firstUpdate.current) {
			firstUpdate.current = false;
			return;
		}
		const date = new Date();
		const expires = addDays(date, 365);
		setCookie("cookieColorMode", mode, { path: "/", expires, secure: true });
	}, [mode, setCookie]);

	React.useEffect(() => {
		const colorSetting = cookies.cookieColorMode;
		if (colorSetting) {
			setThemeThunk(colorSetting as string);
			setMode(colorSetting as PaletteMode);
			if (validateClientSession(session)) {
				const userId: ObjectId = new ObjectId(session.user.dbUser._id);
				fetch(process.env.PRODUCTION_API_URL + "update-theme", {
					method: "POST",
					headers: {
						"Content-Type": "text/plain",
					},
					body: JSON.stringify({
						user_id: userId.toString(),
						theme: colorSetting as string,
					}),
					cache: "no-store",
				});
			}
		}
		// eslint-disable-next-line react-hooks/exhaustive-deps
	}, [cookies.cookieColorMode]);

	const [showInactivityDialog, setShowInactivityDialog] = React.useState(false);

	const [newNotification, setNewNotification] = React.useState(null);

	const [newChatMessage, setNewChatMessage] = React.useState(null);

	/* Let's describe a global useEffect to handle sessions */

	let sessionManager = React.useRef<any>();

	let sessionManagerRealtimeServer = React.useRef<any>();

	/* Session Websocket Connection */

	const onConnectionOpen = React.useCallback(
		(event: any) => {
			/* As soon as the websocket connection opens, let's register a setInterval to send a heartbeat to the server and keep the session alive */

			setShowInactivityDialog(false);

			const heartbeat = setInterval(
				() => {
					sessionManager?.current?.send(
						JSON.stringify({
							sessionId: session.session_id,
						}),
					);
				},
				parseInt(process.env.SESSION_HEARTBEAT_INTERVAL || "10000"),
			);

			return () => {
				clearInterval(heartbeat);
			};
		},
		[session],
	);

	const onMessage = React.useCallback((event: any) => {
		switch (JSON.parse(event.data.toString()).action) {
			case "invalidate-session":
				signOut({
					callbackUrl: "/",
				});
				break;
			case undefined:
				console.log("%c ❤️~Session~Heartbeat~❤️", "color: #FF0000");
				break;
			// case "new-chat-message":
			// 	setNewChatMessage(JSON.parse(event.data.toString()).payload);
			// 	break;
			case "new-notification":
				setNewNotification(JSON.parse(event.data.toString()).payload);
				break;
			default:
				console.error("Unknown message received from server: ", event.data);
				break;
		}
	}, []);

	const onWebSocketError = React.useCallback((event: any) => {
		/*

    	& Ask the user to refresh the page in case of an error through a dialog box.
		! Boolean switch for inactivity dialog feature. Turned off on Tuesday, March the 4th 2025 at 01:00:00 AM.

    	*/

		setShowInactivityDialog(FEATURE_SWITCH_INACTIVITY_DIALOG);
	}, []);

	/*

	& Both sockets (chat audit D9 / D10, 17 Oct 2026). Each effect used to bail when its ref was
	& already set, while its cleanup removed the listeners: `session` is a new object after a
	& client-side navigation, so the cleanup ran, the re-run bailed, and the socket stayed open
	& with no listeners. Live chat (and the session's invalidate-session) stopped after the first
	& navigation. Now each socket is owned by one effect keyed on the session id: opened once,
	& its listeners read the latest handlers through refs, and it is closed (with its heartbeat)
	& when the session id changes or the app unmounts. The chat socket also reconnects after a
	& drop, with backoff, and no longer sends itself a heartbeat frame: the relay keeps the
	& connection alive with its own pings (marketplace-chat-wss, chat audit 1.1).

	*/

	const liveSessionId: string = validateClientSession(session) && session?.session_id ? String(session.session_id) : "";

	const onConnectionOpenRef = React.useRef(onConnectionOpen);
	onConnectionOpenRef.current = onConnectionOpen;
	const onMessageRef = React.useRef(onMessage);
	onMessageRef.current = onMessage;
	const onWebSocketErrorRef = React.useRef(onWebSocketError);
	onWebSocketErrorRef.current = onWebSocketError;

	React.useEffect(() => {
		if (!liveSessionId) return;
		const socket: WebSocket = new WebSocket("wss://session.beegru.com?sessionId=" + liveSessionId);
		sessionManager.current = socket;
		// onConnectionOpen starts the heartbeat and hands back its stop.
		let stopHeartbeat: (() => void) | null = null;
		const handleOpen = (event: any) => {
			stopHeartbeat = onConnectionOpenRef.current(event);
		};
		const handleMessage = (event: any) => onMessageRef.current(event);
		const handleError = (event: any) => onWebSocketErrorRef.current(event);
		const handleClose = () => {
			if (stopHeartbeat) stopHeartbeat();
			stopHeartbeat = null;
		};
		socket.addEventListener("open", handleOpen);
		socket.addEventListener("message", handleMessage);
		socket.addEventListener("error", handleError);
		socket.addEventListener("close", handleClose);
		return () => {
			socket.removeEventListener("open", handleOpen);
			socket.removeEventListener("message", handleMessage);
			socket.removeEventListener("error", handleError);
			socket.removeEventListener("close", handleClose);
			handleClose();
			socket.close();
			if (sessionManager.current === socket) sessionManager.current = undefined;
		};
	}, [liveSessionId]);

	/* Chat Websocket Connection */

	const onMessageRealtimeServer = React.useCallback((event: any) => {
		switch (JSON.parse(event.data.toString()).action) {
			case undefined:
				console.log("%c ❤️~Realtime Session Heartbeat~❤️", "color: #FF0000");
				break;
			case "new-chat-message":
			// A reaction (18 Oct 2026): the chat drawer re-reads that one message's reactions.
			case "message-reaction":
				setNewChatMessage(JSON.parse(event.data.toString()));
				break;
			case "new-notification":
				setNewNotification(JSON.parse(event.data.toString()));
				break;
			// Receipts (18 Oct 2026): the chat drawer re-reads the open chat's ticks.
			case "chat-read":
			case "chat-delivered":
				setNewChatMessage(JSON.parse(event.data.toString()));
				break;
			// A message deleted for everyone (18 Oct 2026): the chat drawer re-reads it.
			case "message-deleted":
			// An edited message (18 Oct 2026): the drawer re-reads it too.
			case "message-edited":
			// The other side is typing (18 Oct 2026): "typing…" for a few seconds.
			case "typing":
				setNewChatMessage(JSON.parse(event.data.toString()));
				break;
			default:
				console.error("Unknown message received from server: ", event.data);
				break;
		}
	}, []);

	const onWebSocketErrorRealtimeServer = React.useCallback((event: any) => {
		console.error("WebSocket error: ", event);
	}, []);

	const onMessageRealtimeServerRef = React.useRef(onMessageRealtimeServer);
	onMessageRealtimeServerRef.current = onMessageRealtimeServer;
	const onWebSocketErrorRealtimeServerRef = React.useRef(onWebSocketErrorRealtimeServer);
	onWebSocketErrorRealtimeServerRef.current = onWebSocketErrorRealtimeServer;

	/* The chat socket connects by the signed-in USER id (chat audit 3.3, 17 Oct 2026), the id the
	   server relays to (lib/chat/realtimeRelay.ts), with a relay token when the relay checks them
	   (chat/relay-token, lib/chat/relayToken.ts). A fresh token is fetched for every (re)connect,
	   since it is only checked when a connection opens. */
	const liveUserId: string = liveSessionId && session?.user?.dbUser?._id ? String(session.user.dbUser._id) : "";

	/* Online / last seen in chat (18 Oct 2026, lib/chat/presence.ts): every minute while this tab is
	   visible and someone is signed in, they are "open" (chat/heartbeat). A hidden tab stops, so the
	   other side soon reads "last seen". */
	React.useEffect(() => {
		if (!liveUserId) return;
		const beat = () => {
			if (typeof document !== "undefined" && document.visibilityState !== "visible") return;
			fetch(process.env.PRODUCTION_API_URL + "chat/heartbeat", { method: "POST", cache: "no-store", body: "{}" }).catch(() => {});
		};
		beat();
		const timer = setInterval(beat, 60 * 1000);
		const onVisible = () => {
			if (document.visibilityState === "visible") beat();
		};
		document.addEventListener("visibilitychange", onVisible);
		return () => {
			clearInterval(timer);
			document.removeEventListener("visibilitychange", onVisible);
		};
	}, [liveUserId]);

	React.useEffect(() => {
		if (!liveUserId) return;
		let stopped: boolean = false;
		let attempt: number = 0;
		let retryTimer: ReturnType<typeof setTimeout> | null = null;
		let socket: WebSocket | null = null;
		const handleMessage = (event: any) => onMessageRealtimeServerRef.current(event);
		const handleError = (event: any) => onWebSocketErrorRealtimeServerRef.current(event);
		// "" when the relay doesn't check tokens yet, or the request fails: connect by user id alone.
		const relayToken = async (): Promise<string> => {
			try {
				const response = await fetch(process.env.PRODUCTION_API_URL + "chat/relay-token", {
					method: "POST",
					cache: "no-store",
				});
				const data = await response.json();
				return data && data.status === "success" && data.payload?.token ? String(data.payload.token) : "";
			} catch {
				return "";
			}
		};
		const connect = async () => {
			const token: string = await relayToken();
			if (stopped) return;
			const next: WebSocket = new WebSocket(
				"wss://chat.wss.beegru.com/?userId=" +
					encodeURIComponent(liveUserId) +
					(token ? "&token=" + encodeURIComponent(token) : ""),
			);
			socket = next;
			// Senders read the live socket through this ref (ChatInput, ImageStackUploadPreview).
			sessionManagerRealtimeServer.current = next;
			next.addEventListener("open", () => {
				attempt = 0;
			});
			next.addEventListener("message", handleMessage);
			next.addEventListener("error", handleError);
			// A drop reconnects with backoff (1s doubling to 30s); a close we asked for does not.
			next.addEventListener("close", () => {
				if (stopped || socket !== next) return;
				const delay: number = Math.min(30000, 1000 * Math.pow(2, Math.min(attempt, 5)));
				attempt += 1;
				retryTimer = setTimeout(connect, delay);
			});
		};
		connect();
		return () => {
			stopped = true;
			if (retryTimer) clearTimeout(retryTimer);
			const current: WebSocket | null = socket;
			if (current) {
				current.removeEventListener("message", handleMessage);
				current.removeEventListener("error", handleError);
				current.close();
			}
			if (sessionManagerRealtimeServer.current === current) sessionManagerRealtimeServer.current = undefined;
		};
	}, [liveUserId]);

	const theme = React.useMemo(() => createTheme(GetDesignTokens(mode)), [mode]);

	return (
		<SessionProvider session={pageProps.session ? pageProps.session : pageProps.server_session}>
			<CacheProvider value={emotionCache}>
				<ColorModeContext.Provider value={colorMode}>
					<ThemeProvider theme={theme}>
						<CssBaseline />
						<GlobalStyles
							styles={{
								/* Thin styled scrollbar for the main page */
								"html, body": {
									scrollbarWidth: "thin",
									scrollbarColor: "transparent transparent",
								},
								"html:hover, body:hover": {
									scrollbarColor: "#C4C4C4 transparent",
								},
								"html::-webkit-scrollbar, body::-webkit-scrollbar": {
									width: "6px",
									height: "6px",
								},
								"html::-webkit-scrollbar-track, body::-webkit-scrollbar-track": {
									background: "transparent",
								},
								"html::-webkit-scrollbar-thumb, body::-webkit-scrollbar-thumb": {
									background: "transparent",
									borderRadius: "3px",
									transition: "background 0.3s ease",
								},
								"html:hover::-webkit-scrollbar-thumb, body:hover::-webkit-scrollbar-thumb": {
									background: "#C4C4C4",
								},
								"html::-webkit-scrollbar-thumb:hover, body::-webkit-scrollbar-thumb:hover": {
									background: "#A0A0A0",
								},
								/* Hide scrollbars on all other elements */
								":where(*)": {
									scrollbarWidth: "none",
								},
								":where(*)::-webkit-scrollbar": {
									display: "none",
								},
							}}
						/>
						<CookiesProvider cookies={isBrowser ? undefined : new Cookies(cookies)}>
							<Provider store={store}>
								<Component
									{...pageProps}
									theme={theme}
									showInactivityDialog={showInactivityDialog}
									newNotification={newNotification}
									newChatMessage={newChatMessage}
									realtimeWebsocket={sessionManagerRealtimeServer}
								/>
								<Analytics
									beforeSend={(event) => {
										try {
											const raw = localStorage.getItem("cmp_consent");
											if (!raw) return null;
											const consent = JSON.parse(raw);
											if (!consent.analytics) return null;
										} catch {
											return null;
										}
										return event;
									}}
								/>
							</Provider>
						</CookiesProvider>
					</ThemeProvider>
				</ColorModeContext.Provider>
			</CacheProvider>
		</SessionProvider>
	);
};

/*

& Let's define a global getInitialProps to validate the session server-side.

^ We're only doing this because _app.ts does not support getStaticProps() or getServerSideProps() yet.

? For more information, visit https://nextjs.org/docs/advanced-features/custom-app#caveats

! This method no longer works as getInitialProps doesn't include the req and res objects in production anymore. This causes any attempts to validate the session server-side to fail. Moving forward, all session validation will be done in getServerSideProps on each page where the session is needed.

*/

/* Let's wrap the entire app with redux to make life easier :) */
/* UPDATE: The new implementation destructures and creates a store within the function definition and therefore a wrapper function is no longer needed. */

export default Marketplace;
